/**
 * RadioCard — Tier 0 slice-local primitive (SCRUM-167 epic).
 *
 * Card-styled radio used in the Create wizard (Step 1 Type + Step 4
 * Units composition) and the Unit Status modal (Operational /
 * Availability fieldsets per `STATUS_MATRIX.md`).
 *
 * The `disabled` branch is load-bearing for the status modal's hard
 * guard — when the user selects Inactive, the Bookable RadioCard
 * becomes disabled (cursor + opacity + non-clickable) and a `Not
 * allowed` badge is rendered inline.
 *
 * Native `<input type="radio">` lives inside a labelled card so the
 * whole card is clickable and keyboard-focusable. The radio itself
 * is visually hidden (sr-only) — the card's selected state is the
 * visual indicator. Focus ring wraps the card per ACCESSIBILITY.md §3.
 */

import * as React from 'react';
import { Badge } from '@sparta/ui-kit';

export interface RadioCardProps {
  readonly checked: boolean;
  readonly disabled?: boolean;
  /** The form name groups multiple radios. */
  readonly name: string;
  /** The radio's value within the group. */
  readonly value: string;
  /** Visible title (first line). */
  readonly title: string;
  /** Optional secondary copy (second line). */
  readonly sub?: string;
  /** Optional inline badge — used for "Not allowed" on disabled options. */
  readonly badge?: string;
  readonly onChange: (value: string) => void;
  /** Optional `aria-describedby` for inline alert wiring. */
  readonly 'aria-describedby'?: string;
}

export function RadioCard({
  checked,
  disabled = false,
  name,
  value,
  title,
  sub,
  badge,
  onChange,
  'aria-describedby': ariaDescribedby,
}: RadioCardProps): React.ReactElement {
  return (
    <label
      className="block cursor-pointer rounded-xl p-4 transition-colors min-h-20"
      // eslint-disable-next-line no-restricted-syntax -- SCRUM-167: opacity is a prop-driven design-system value (disabled state, ACCESSIBILITY.md §8 + per the bundle's source/properties.jsx line 546). Border/background/cursor are token-resolved.
      style={{
        background: checked ? 'var(--bg-surface)' : 'var(--bg-sunken)',
        border: `1px solid ${
          checked ? 'var(--border-focus)' : 'var(--border-subtle)'
        }`,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}
      // The bundle calls for a min-height of ≥76px on modal RadioCards
      // (ACCESSIBILITY.md §8 touch-target floor). Use the canonical
      // Tailwind `min-h-20` (80px) — the nearest step, exceeds the
      // 76px floor.
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        aria-describedby={ariaDescribedby}
        onChange={(e) => onChange(e.target.value)}
        className="sr-only"
      />
      <div className="flex items-center justify-between gap-2">
        <span
          className="text-sm font-semibold"
          style={{ color: 'var(--fg-primary)' }}
        >
          {title}
        </span>
        {badge !== undefined ? (
          <Badge variant="danger" size="sm">
            {badge}
          </Badge>
        ) : null}
      </div>
      {sub !== undefined ? (
        <p
          className="m-0 mt-1 text-xs"
          style={{ color: 'var(--fg-secondary)' }}
        >
          {sub}
        </p>
      ) : null}
    </label>
  );
}
